import {Helmet} from '@dr.pogodin/react-helmet';
import {i18n} from '@online-offer/i18n';
import {Box, Grid, GridItem, Heading, HStack, Image, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {css} from 'styled-components';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {parseDate} from 'shared/web';

import {IllustrativePhotoCaption} from '../../components/IllustrativePhotoCaption/IllustrativePhotoCaption';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {getHeroPhotos} from '../../utils/getHeroPhotos';
import {getWorkspaceCustomization} from '../../utils/getWorkspaceCustomization';
import {makeStorageResizeUrl} from '../../utils/makeStorageResizeUrl';

const LOGO_WIDTH = 220 * 2;
const LOGO_HEIGHT = 44 * 2;
const HERO_PHOTOS_COUNT = 3;
const HERO_PHOTO_WIDTH = 400;

export function HeroPrint() {
  const {logo: applicationLogo} = getWorkspaceCustomization();
  const {
    saleVehiclePhotos,
    illustrativePhoto,
    vehicle,
    tenantLogoUrl,
    activeReservation,
    customer,
    offerValidTo,
  } = useGetOnlineOfferData();
  const formatDateTime = useDateTimeFormatter();
  const customerName = activeReservation?.customer?.name ?? customer;
  const formattedOfferValidTo = offerValidTo
    ? formatDateTime('dateShort', parseDate(offerValidTo))
    : null;

  const resizedLogoUrl = tenantLogoUrl
    ? makeStorageResizeUrl(tenantLogoUrl, {
        width: LOGO_WIDTH,
        height: LOGO_HEIGHT,
        withoutEnlargement: true,
      })
    : undefined;

  const {photos, isIllustrative} = getHeroPhotos({saleVehiclePhotos, illustrativePhoto});
  const photosToShow = photos.slice(0, HERO_PHOTOS_COUNT);

  return (
    <div
      css={css`
        page-break-inside: avoid;
      `}
    >
      <Box width="100%" overflow="hidden" paddingHorizontal={10} paddingVertical={5}>
        <VStack spacing={10} width="100%" height="100%">
          <Helmet>
            <title>{vehicle?.title}</title>
          </Helmet>
          <HStack>
            {resizedLogoUrl ? <Image maxHeight={11} src={resizedLogoUrl} /> : applicationLogo}
          </HStack>
          <Heading size={1}>{vehicle?.title}</Heading>

          <Show when={isNotNilOrEmpty(customerName)}>
            <VStack spacing={1}>
              <Text size="small" color="secondary">
                {i18n.t('navigation.offerFor', {
                  code: activeReservation?.businessCase?.code ?? '',
                })}
              </Text>
              <Heading size={4}>{customerName}</Heading>
              <Show when={isNotNilOrEmpty(formattedOfferValidTo)}>
                <Text size="small" color="secondary">
                  {i18n.t('navigation.offerValidTo')} {formattedOfferValidTo}
                </Text>
              </Show>
              <Show when={activeReservation}>
                {(activeReservation) => (
                  <Text size="small" color="secondary">
                    {i18n.t('timerCard.reservedUntilFull', {
                      date: formatDateTime(
                        'dateTimeShort',
                        parseDate(activeReservation.reservedUntilAt)
                      ),
                    })}
                  </Text>
                )}
              </Show>
            </VStack>
          </Show>

          <Show when={isNotNilOrEmpty(photosToShow)}>
            <VStack spacing={1}>
              <Grid columns={3} horizontalSpacing={2}>
                {photosToShow.map((photo) => (
                  <GridItem key={photo.id}>
                    <Image
                      fit="cover"
                      position="center"
                      ratio="3 / 2"
                      width="100%"
                      borderRadius="small"
                      src={makeStorageResizeUrl(photo.url, {width: HERO_PHOTO_WIDTH})}
                      alt={vehicle?.title}
                    />
                  </GridItem>
                ))}
              </Grid>
              <Show when={isIllustrative}>
                <IllustrativePhotoCaption />
              </Show>
            </VStack>
          </Show>
        </VStack>
      </Box>
    </div>
  );
}
